import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Typography, Alert } from 'antd';
import {
  EnvironmentOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';

const { Title } = Typography;

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [reportStats, setReportStats] = useState(null);
  const [error, setError] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    if (user?.current_company_id || user?.role === 'super_admin') {
      fetchDashboardData();
    }
  }, [user?.current_company_id, user?.role]); // Re-fetch when company changes

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      if (user?.role === 'super_admin') {
        // Super admin - get system-wide stats
        const companyStatsRes = await api.get('/superadmin/stats');

        if (companyStatsRes.data.success) {
          const data = companyStatsRes.data.data;
          // Flatten for easy access: overall + checks
          setStats({
            total_companies: data.overall?.total_companies || 0,
            total_users: data.overall?.total_users || 0,
            total_guards: data.overall?.total_guards || 0,
            total_checkpoints: data.overall?.total_checkpoints || 0,
            companies: data.companies || [],
          });
          // Use checks data from stats (already computed server-side)
          setReportStats({
            overall: {
              completed_checks: Number(data.checks?.completed_checks || 0),
              missed_checks: Number(data.checks?.missed_checks || 0),
              unable_checks: Number(data.checks?.unable_checks || 0),
              completion_rate: Math.round(Number(data.checks?.completion_rate || 0)),
            },
            by_checkpoint: [],
            by_guard: [],
          });
        }
      } else {
        // Company admin - get company-specific stats
        const [statsRes, reportsRes] = await Promise.all([
          api.get('/companies/stats'),
          api.get('/reports/stats')
        ]);

        if (statsRes.data.success) {
          setStats(statsRes.data.data);
        }

        if (reportsRes.data.success) {
          setReportStats(reportsRes.data.data);
        }
      }

    } catch (error) {
      console.error('Dashboard data fetch error:', error);
      setError('ไม่สามารถโหลดข้อมูลหน้าแดชบอร์ดได้');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner tip="กำลังโหลดข้อมูลหน้าแดชบอร์ด..." />;
  }

  if (error) {
    return (
      <Alert
        message="ข้อผิดพลาด"
        description={error}
        type="error"
        showIcon
        action={
          <button onClick={fetchDashboardData}>ลองใหม่</button>
        }
      />
    );
  }

  const hasPieData = reportStats?.overall && (
    Number(reportStats.overall.completed_checks) > 0 ||
    Number(reportStats.overall.missed_checks) > 0 ||
    Number(reportStats.overall.unable_checks) > 0
  );

  const pieData = hasPieData ? [
    { name: 'สำเร็จ', value: Number(reportStats.overall.completed_checks), color: '#52c41a' },
    { name: 'ขาด/พลาด', value: Number(reportStats.overall.missed_checks), color: '#f5222d' },
    { name: 'ไม่สามารถตรวจได้', value: Number(reportStats.overall.unable_checks), color: '#faad14' }
  ] : [];

  const completionRate = reportStats?.overall?.completion_rate || 0;

  return (
    <div>
      <div className="dashboard-header">
        <Title level={1}>ยินดีต้อนรับกลับมา, {user?.name}!</Title>
        <p>{user?.role === 'super_admin' ?
          "ภาพรวมจากทุกระบบการปฏิบัติงานของเจ้าหน้าที่ รปภ." :
          "นี่คือสถานะปัจจุบันในระบบเดินตรวจของคุณ"}</p>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {user?.role === 'super_admin' ? (
          <>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stats-card">
                <Statistic
                  title="บริษัททั้งหมด"
                  value={stats?.total_companies || 0}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="stats-card">
                <Statistic
                  title="จำนวน รปภ. ทั้งหมด"
                  value={stats?.total_guards || 0}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="stats-card">
                <Statistic
                  title="จุดตรวจทั้งหมด"
                  value={stats?.total_checkpoints || 0}
                  prefix={<EnvironmentOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="stats-card">
                <Statistic
                  title="อัตราการตรวจสำเร็จโดยรวม"
                  value={completionRate}
                  suffix="%"
                  prefix={<ExclamationCircleOutlined />}
                  valueStyle={{
                    color: completionRate >= 90 ? '#52c41a' :
                      completionRate >= 70 ? '#faad14' : '#f5222d'
                  }}
                />
              </Card>
            </Col>
          </>
        ) : (
          <>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stats-card">
                <Statistic
                  title="จุดตรวจที่ใช้งาน"
                  value={stats?.checkpoints || 0}
                  prefix={<EnvironmentOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="stats-card">
                <Statistic
                  title="รปภ. ที่ปฏิบัติงาน"
                  value={stats?.guards || 0}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="stats-card">
                <Statistic
                  title="การตรวจวันนี้"
                  value={stats?.todayStats?.total_checks || 0}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="stats-card">
                <Statistic
                  title="อัตราความสำเร็จ"
                  value={completionRate}
                  suffix="%"
                  prefix={<ExclamationCircleOutlined />}
                  valueStyle={{
                    color: completionRate >= 90 ? '#52c41a' :
                      completionRate >= 70 ? '#faad14' : '#f5222d'
                  }}
                />
              </Card>
            </Col>
          </>
        )}
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title={user?.role === 'super_admin' ? "บริษัทที่มีประสิทธิภาพสูงสุด" : "จุดตรวจที่มีประสิทธิภาพสูงสุด"} style={{ height: 400 }}>
            {user?.role === 'super_admin' ? (
              stats?.companies && stats.companies.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={stats.companies}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="company_name"
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="avg_completion_rate"
                      stroke="#1890ff"
                      strokeWidth={2}
                      dot={{ fill: '#1890ff', strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, color: '#999' }}>
                  ไม่มีข้อมูลบริษัท
                </div>
              )
            ) : (
              reportStats?.by_checkpoint && reportStats.by_checkpoint.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={reportStats.by_checkpoint.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="checkpoint_name"
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="completion_rate"
                      stroke="#1890ff"
                      strokeWidth={2}
                      dot={{ fill: '#1890ff', strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, color: '#999' }}>
                  ไม่มีข้อมูลจุดตรวจ
                </div>
              )
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="สถานะและสัดส่วนการตรวจรอบนี้" style={{ height: 400 }}>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={120}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 300,
                color: '#999'
              }}>
                ไม่มีข้อมูลสถานะการตรวจ
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {reportStats?.by_guard && reportStats.by_guard.length > 0 && (
        <Card title="ประสิทธิภาพการทำงาน รปภ." style={{ marginTop: 16 }}>
          <Row gutter={[16, 16]}>
            {reportStats.by_guard.slice(0, 6).map((guard, index) => (
              <Col xs={12} sm={8} lg={4} key={guard.guard_id || guard.id || guard.guard_name || `guard-${index}`}>
                <Card size="small" className="stats-card">
                  <Statistic
                    title={guard.guard_name}
                    value={guard.completion_rate || 0}
                    suffix="%"
                    valueStyle={{
                      fontSize: 20,
                      color: guard.completion_rate >= 90 ? '#52c41a' :
                        guard.completion_rate >= 70 ? '#faad14' : '#f5222d'
                    }}
                  />
                  <p style={{ margin: 0, fontSize: 12, color: '#999' }}>
                    สำเร็จ {guard.completed_checks}/{guard.total_checks} จุด
                  </p>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;